import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import PropTypes from 'prop-types'
import { push } from '@lagunovsky/redux-react-router'
import { getSandreList } from '../../../utils/StoreUtils'
import AgriAction from '../../../agriAdministration/actions/AgriAction'
import ReferencialAction from 'referencial/action/ReferencialAction'
import DtoSurveyWithStats from '../../dto/DtoSurveyWithStats'
import { isTrue } from '../../../utils/SettingUtils'
import useActions from 'utils/customHook/useActions'
import useTitle from 'utils/customHook/useTitle'
import Input from 'components/forms/Input'
import Textarea from 'components/forms/Textarea'
import { Button, Card, CardContent, Dialog, DialogActions, DialogContent, Grid, Stack, Tooltip } from '@mui/material'
import DtoManagementUnit from 'referencial/components/managementUnit/dto/DtoManagementUnit'
import Select from 'components/forms/Select'
import DtoRuleScenarios from 'agriAdministration/dto/DtoRuleScenarios'
import { nbPerPageLabelShort } from 'referencial/constants/ReferencialConstants'
import { formatMilliers } from 'utils/StringUtil'
import { compact, isNil, isUndefined, keyBy, orderBy, round, uniq } from 'lodash'
import Checkbox from 'components/forms/Checkbox'
import SimpleCheckbox from '../../../components/forms/SimpleCheckbox'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import ToastrAction from 'toastr/actions/ToastrAction'
import ProgressCard from 'components/card/ProgressCard'
import NumberField from 'components/forms/NumberField'
import { OPERATOR_TYPES, PERIODES, SAMPLE_TYPES, UNIT_TYPES, colorLowWater, colorNotLowWater } from 'agriAdministration/constants/AgriConstants'
import EChart from 'components/echart/EChart'
import Axis from 'components/echart/Axis'
import Line from 'components/echart/series/Line'
import { useParams } from 'react-router'
import { CardTable } from 'components/datatable/NewTable'
import { darkBlue, mainBlue, secondaryBlue } from 'utils/constants/ColorTheme'
import { Info } from '@mui/icons-material'
import useApplicationSetting, { booleanParser } from 'utils/customHook/useApplicationSetting'
import { CardTitle } from 'components/card/NewCard'
import { usePDF } from 'react-to-pdf'
import SimpleInput, { INPUT_TYPES } from 'components/forms/SimpleInput'
import SimpleSelect from 'components/forms/SimpleSelect'
import Other from 'components/actions/Other'
import SimpleTextArea from 'components/forms/SimpleTextArea'

const RuleDialog = ({
    rule = {},
    onClose = () => {},
    onChange = () => {},
    onValidate = () => {},
    datasRules = [],
    datasDiffRules = [],
}) => {
    const [tmpSearchValue, setTmpSearchValue] = useState()
    const [searchValue, setSearchValue] = useState()

    const dispatch = useDispatch()

    const {
        cultures,
        culturesFamilies,
        usages,
        installationsWithGeo,
        exploitationsExportFullData,
        modesIrrigations,
        PAR,
    } = useSelector(store => ({
        cultures: store.CultureReducer.cultures,
        culturesFamilies: store.CulturesFamilyReducer.culturesFamilies,
        usages: store.UsageReducer.usages,
        installationsWithGeo: store.InstallationReducer.installationsWithGeo,
        exploitationsExportFullData: store.AgriReducer.exploitationsExportFullData,
        modesIrrigations: store.AgriReducer.modesIrrigations,
        PAR: store.AgriReducer.PAR,
    }), shallowEqual)

    const { label, description, parameters: { culture, modeIrrigation, sampleType, usage, operator, value, unit, exceptions, periode } } = rule

    const filteredPoints = useMemo(() => {
        const cultureFiltered = culture ? datasRules.filter((i) => i.idCulture === culture) : datasRules
        const usageFiltered = usage ? cultureFiltered.filter((i) => i.idUsage === usage) : cultureFiltered
        const sampleTypeFiltered = sampleType ? usageFiltered.filter((i) => i.typePrel === sampleType) : usageFiltered
        const irrigModeFiltered = modeIrrigation ? sampleTypeFiltered.filter((i) => i.irrigationMode === modeIrrigation) : sampleTypeFiltered

        return orderBy(irrigModeFiltered.map((d) => {
            const exploitation = exploitationsExportFullData.find((e) => e.idExploitation === d.idExploitation)
            const inst = installationsWithGeo.find((i) => i.id === d.idInstallation)

            const instDatasDiff = datasDiffRules.filter((i) => i.idExploitation === d.idExploitation && i.idInstallation === d.idInstallation && i.idRule < 0)
            const skimmedVolumeLowWater = d.volumeAskedLowWater - (instDatasDiff.reduce((acc, i) => acc + i.volumeDiffLowWater, 0))
            const skimmedVolumeNotLowWater = d.volumeAskedNotLowWater - (instDatasDiff.reduce((acc, i) => acc + i.volumeDiffNotlowWater, 0))

            const exempt = (exceptions || []).find((ex) => ex.idExploitation === exploitation.idExploitation && ex.idInstallation === d.idInstallation)

            const [correctedVolumeLowWater, correctedVolumeNotLowWater] = (() => {
                if (exempt) {
                    return [skimmedVolumeLowWater, skimmedVolumeNotLowWater]
                }
                const valueRule = Number((operator === OPERATOR_TYPES.ADD ? '+' : '-') + (value || 0))
                if (unit === UNIT_TYPES.VOLUME) {
                    return [skimmedVolumeLowWater + valueRule, skimmedVolumeNotLowWater + valueRule]
                }
                return [skimmedVolumeLowWater + (skimmedVolumeLowWater * (valueRule / 100)), skimmedVolumeNotLowWater + (skimmedVolumeNotLowWater * (valueRule / 100))]
            })()
            const correctedVolumeLowWaterWithPeriode = periode === PERIODES.NOT_LOW_WATER ? skimmedVolumeLowWater : (correctedVolumeLowWater > 0 ? round(correctedVolumeLowWater) : 0)
            const correctedVolumeNotLowWaterWithPeriode = periode === PERIODES.LOW_WATER ? skimmedVolumeNotLowWater : (correctedVolumeNotLowWater > 0 ? round(correctedVolumeNotLowWater) : 0)

            return {
                isSelected: !exempt,
                idExploitation: exploitation.idExploitation,
                idInstallation: d.idInstallation,
                checkBox: (
                    <Grid container justifyContent={'center'}>
                        <Checkbox
                            col
                            checked={!exempt}
                            onChange={() => onChange({
                                ...rule,
                                parameters: {
                                    ...rule.parameters,
                                    exceptions: compact([
                                        ...(exceptions || []).filter((ex) => !(ex.idExploitation === exploitation.idExploitation && ex.idInstallation === d.idInstallation)),
                                        exempt ? null : { idExploitation: exploitation.idExploitation, idInstallation: d.idInstallation },
                                    ]),
                                },
                            })}
                        />
                    </Grid>
                ),
                exploitation: `[${exploitation.codification}] ${exploitation.name}`,
                point: inst.code,
                askedVolumeLowWater: formatMilliers(d.volumeAskedLowWater || 0),
                askedVolumeNotLowWater: formatMilliers(d.volumeAskedNotLowWater || 0),
                skimmedVolumeLowWater: formatMilliers(skimmedVolumeLowWater > 0 ? round(skimmedVolumeLowWater) : 0),
                skimmedVolumeNotLowWater: formatMilliers(skimmedVolumeNotLowWater > 0 ? round(skimmedVolumeNotLowWater) : 0),
                correctedVolumeLowWater: formatMilliers(round(correctedVolumeLowWaterWithPeriode)),
                correctedVolumeNotLowWater: formatMilliers(round(correctedVolumeNotLowWaterWithPeriode)),
                allocatedVolume: formatMilliers(round(correctedVolumeLowWaterWithPeriode + correctedVolumeNotLowWaterWithPeriode)),
            }
        }).filter((e) => searchValue ? (`${e.exploitation} ${e.name} ${e.point}`).includes(searchValue) : true), 'exploitation')
    }, [datasRules, datasDiffRules, exploitationsExportFullData, installationsWithGeo, rule, sampleType, exceptions, searchValue, onChange, operator, value, unit, culture, usage, modeIrrigation, periode])

    const onValidateCheck = () => {
        if (label && operator && value && unit && periode) {
            onValidate()
            onClose()
        } else {
            dispatch(ToastrAction.error(i18n.pleaseCompleteAllRequiredField))
        }
    }

    const culturesFormatted = useMemo(() => {
        return cultures.map((c) => ({
            ...c,
            cultureWithFamily: `[${(culturesFamilies.find((cF) => cF.id === c.idFamille))?.name}] ${c.name}`,
        }))
    }, [cultures, culturesFamilies])

    const isAllSelected = filteredPoints.every(p => p.isSelected)

    const onSelectAll = () => {
        onChange({
            ...rule,
            parameters: {
                ...rule.parameters,
                exceptions: isAllSelected ? uniq([
                    ...(exceptions || []),
                    ...filteredPoints.map((p) => ({ idExploitation: p.idExploitation, idInstallation: p.idInstallation })),
                ]) : [],
            },
        })
    }

    return (
        <Dialog
            fullWidth
            open
            maxWidth='lg'
        >
            <DefaultDialogTitle title={i18n.ruleManagement} onClose={onClose} />
            <DialogContent>
                <Card>
                    <CardTitle title={i18n.ruleSettings} />
                    <CardContent>
                        <Grid container spacing={1} sx={{ padding: '5px' }}>
                            <Grid size={2}>
                                <Input
                                    title={i18n.name}
                                    value={label}
                                    onChange={v => onChange({ ...rule, label: v })}
                                    obligatory
                                />
                            </Grid>
                            <Grid size={2}>
                                <Select
                                    label={i18n.culture}
                                    options={culturesFormatted}
                                    value={culture}
                                    onChange={v => onChange({ ...rule, parameters: { ...rule.parameters, culture: v } })}
                                    integerValue
                                    keyValue='id'
                                    keyLabel='cultureWithFamily'
                                    nullLabel='&nbsp;'
                                />
                            </Grid>
                            <Grid size={2}>
                                <Select
                                    label={i18n.usage}
                                    options={usages}
                                    value={usage}
                                    onChange={v => onChange({ ...rule, parameters: { ...rule.parameters, usage: v } })}
                                    integerValue
                                    keyValue='idUsage'
                                    keyLabel='description'
                                    nullLabel='&nbsp;'
                                />
                            </Grid>
                            <Grid size={3}>
                                <Select
                                    label={i18n.sampleType}
                                    options={[{
                                        value: SAMPLE_TYPES.UNDERGROUND,
                                        label: i18n.underground,
                                    }, {
                                        value: SAMPLE_TYPES.SUPERFICIAL,
                                        label: i18n.superficial,
                                    }]}
                                    value={sampleType}
                                    onChange={v => onChange({ ...rule, parameters: { ...rule.parameters, sampleType: v } })}
                                    integerValue
                                    keyValue='code'
                                    nullLabel='&nbsp;'
                                />
                            </Grid>
                            <Grid size={3}>
                                <Select
                                    label={i18n.modeIrrigation}
                                    options={modesIrrigations}
                                    value={modeIrrigation}
                                    onChange={v => onChange({ ...rule, parameters: { ...rule.parameters, modeIrrigation: v } })}
                                    integerValue
                                    keyValue='id'
                                    nullLabel='&nbsp;'
                                />
                            </Grid>
                            <Grid size={2}>
                                <Select
                                    label={i18n.periode}
                                    options={[{
                                        value: PERIODES.ALL,
                                        label: i18n.all,
                                    }, {
                                        value: PERIODES.LOW_WATER,
                                        label: i18n.lowWater,
                                    }, {
                                        value: PERIODES.NOT_LOW_WATER,
                                        label: i18n.notLowWater,
                                    }]}
                                    value={periode}
                                    onChange={v => onChange({ ...rule, parameters: { ...rule.parameters, periode: v } })}
                                    disabled={[2, 3].includes(PAR.periode)}
                                    integerValue
                                    noNullValue
                                    obligatory
                                    noSort
                                />
                            </Grid>
                            <Grid size={2}>
                                <Select
                                    label={i18n.calcOperator}
                                    options={[{
                                        value: OPERATOR_TYPES.SUBTRACT,
                                        label: '-',
                                    }, {
                                        value: OPERATOR_TYPES.ADD,
                                        label: '+',
                                    }]}
                                    value={operator}
                                    onChange={v => onChange({ ...rule, parameters: { ...rule.parameters, operator: v } })}
                                    integerValue
                                    nullLabel='&nbsp;'
                                    noNullValue
                                    obligatory
                                />
                            </Grid>
                            <Grid size={2}>
                                <NumberField
                                    title={i18n.value}
                                    value={value}
                                    onChange={v => onChange({ ...rule, parameters: { ...rule.parameters, value: v } })}
                                    obligatory
                                    floatValue
                                />
                            </Grid>
                            <Grid size={2}>
                                <Select
                                    label={i18n.unit}
                                    options={[{
                                        value: UNIT_TYPES.PERCENT,
                                        label: '%',
                                    }, {
                                        value: UNIT_TYPES.VOLUME,
                                        label: 'm3',
                                    }]}
                                    value={unit}
                                    onChange={v => onChange({ ...rule, parameters: { ...rule.parameters, unit: v } })}
                                    integerValue
                                    nullLabel='&nbsp;'
                                    noNullValue
                                    obligatory
                                />
                            </Grid>
                            <Grid size={4}>
                                <Textarea
                                    title={i18n.description}
                                    value={description}
                                    onChange={v => onChange({ ...rule, description: v })}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
                <Card title={i18n.filters} round className='margin-top-1' noMargin={false}>
                    <Grid className='padding-1' container justifyContent='space-between'>
                        <Grid size={4}>
                            <Input
                                containerClassName='no-margin'
                                title={ i18n.search }
                                value={ tmpSearchValue }
                                onChange={(v) => setTmpSearchValue(v)}
                                onEnterKeyPress={(v) => {
                                    setTmpSearchValue(v)
                                    setSearchValue(v)
                                }}
                            />
                        </Grid>
                        <Button variant='contained' onClick={() => setSearchValue(tmpSearchValue)}>
                            {i18n.search}
                        </Button>
                    </Grid>
                </Card>
                <div className='padding-top-1'>
                    <CardTable
                        title={i18n.impactedSamplingPoints}
                        rows={filteredPoints}
                        headers={[
                            { key: 'checkBox', value: (
                                <Grid container justifyContent={'center'}>
                                    <Checkbox
                                        checked={isAllSelected}
                                        onChange={onSelectAll}
                                    />
                                </Grid>
                            ) },
                            'exploitation',
                            'point',
                            { key: 'askedVolumeLowWater', value: <span style={{ whiteSpace: 'pre-wrap' }}>{i18n.askedVolumeLowWaterWrap}</span> },
                            { key: 'askedVolumeNotLowWater', value: <span style={{ whiteSpace: 'pre-wrap' }}>{i18n.askedVolumeNotLowWaterWrap}</span> },
                            { key: 'skimmedVolumeLowWater', value: <span style={{ whiteSpace: 'pre-wrap' }}>{i18n.skimmedVolumeLowWaterWrap}</span> },
                            { key: 'skimmedVolumeNotLowWater', value: <span style={{ whiteSpace: 'pre-wrap' }}>{i18n.skimmedVolumeNotLowWaterWrap}</span> },
                            { key: 'correctedVolumeLowWater', value: <span style={{ whiteSpace: 'pre-wrap' }}>{i18n.correctedVolumeLowWaterWrap}</span> },
                            { key: 'correctedVolumeNotLowWater', value: <span style={{ whiteSpace: 'pre-wrap' }}>{i18n.correctedVolumeNotLowWaterWrap}</span> },
                            { key: 'allocatedVolume', value: <span style={{ whiteSpace: 'pre-wrap' }}>{i18n.attributedVolumeWrap}</span> },
                        ]}
                        rowsPerPageOptions={nbPerPageLabelShort}
                        color={darkBlue}
                    />
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={onValidateCheck} variant='contained' color='primary'>
                    {i18n.validate}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

RuleDialog.propTypes = {
    rule: PropTypes.instanceOf(DtoRuleScenarios).isRequired,
    datasRules: PropTypes.instanceOf([]).isRequired,
    datasDiffRules: PropTypes.instanceOf([]).isRequired,
    onClose: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    onValidate: PropTypes.func.isRequired,
}

const getWaterfallBaseSeries = ({ name, color, volumes }) => Line({
    name,
    type: 'bar',
    stack: 'total',
    silent: true,
    itemStyle: { color },
    data: [
        0,
        volumes.asked - volumes.skimming,
        volumes.asked - volumes.skimming + volumes.arbitration,
        volumes.asked - volumes.skimming + volumes.arbitration,
        0,
        volumes.allocated,
        0,
    ].map((value) => ({ value, itemStyle: { color: 'transparent' } })),
})

const getWaterfallBarSeries = ({ name, color, volumes }) => Line({
    name,
    type: 'bar',
    stack: 'total',
    itemStyle: { color },
    data: [
        volumes.asked,
        volumes.skimming,
        Math.abs(volumes.arbitration),
        volumes.exception,
        volumes.allocated,
        volumes.aup - volumes.allocated,
        volumes.aup,
    ],
})


const ScenarioDashboardApp = () => {
    const { id } = useParams()

    const {
        sandreCodes,
        scenario,
        rulesScenarios,
        PAR,
        surveysWithStats,
        managementUnits,
        accountUser,
    } = useSelector(store => ({
        sandreCodes: store.ReferencialReducer.sandreCodes,
        scenario: store.AgriReducer.scenario,
        rulesScenarios: store.AgriReducer.rulesScenarios,
        PAR: store.AgriReducer.PAR,
        surveysWithStats: store.AgriReducer.surveysWithStats,
        managementUnits: store.ManagementUnitReducer.managementUnits,
        accountUser: store.AccountReducer.accountUser,
    }), shallowEqual)

    const [localScenario, setLocalScenario] = useState({})
    const [localDatasRules, setLocalDatasRules] = useState([])
    const [localDatasDiffRules, setLocalDatasDiffRules] = useState([])
    const [duplicateScenario, setDuplicateScenario] = useState({})
    const [editMode, setEditMode] = useState(false)
    const [openDuplicate, setOpenDuplicate] = useState(false)
    const [rule, setRule] = useState()
    const [openRule, setOpenRule] = useState(false)
    const [dataLoaded, setDataLoaded] = useState(true)

    const dispatch = useDispatch()

    const idScenario = useMemo(() => parseInt(id), [id])

    const uge = useMemo(() => {
        return managementUnits.find((u) => u.managementCode === scenario.managementCode) || {}
    }, [managementUnits, scenario.managementCode])

    const activateEsupEsoutDistinction = !!localScenario.esupEsoutDistinction
    const activateRegulationCoef = useApplicationSetting('activateRegulationCoef', booleanParser)

    const { toPDF, targetRef } = usePDF({
        filename: `Bilan_UG_${uge.id || ''}_${uge.name || ''}_${scenario.name || ''}`,
        method: 'open',
    })

    useEffect(() => {
        dispatch(AgriAction.fetchScenario(idScenario)).then((res) => {
            setLocalScenario(scenario)
            setLocalDatasRules(res?.datasScenario || [])
            setLocalDatasDiffRules(res?.datasDiffScenario || [])
        })
        if (scenario.idPAR && (scenario.idPAR !== PAR.id)) {
            dispatch(AgriAction.fetchPAR(scenario.idPAR))
        }
        if (!sandreCodes.length) {
            dispatch(ReferencialAction.fetchSandreCodes())
        }
        if (!surveysWithStats.length) {
            dispatch(AgriAction.fetchSurveysWithStats())
        }
    }, [scenario.id, idScenario, scenario.idPAR, PAR.id])

    useTitle(() => [{
        title: i18n.planning,
        href: 'planning/dashboard',
    }, {
        title: 'PARs',
        href: 'planning/par',
    }, {
        title: PAR.name,
        href: `par/${PAR.id}`,
    }, {
        title: `[${uge.id || ''}] ${uge.name || ''} - ${scenario.name || ''}`,
        href: `scenario/${scenario.id}`,
    }], [PAR.id, scenario.id, scenario.name, uge])

    const duplicateAction = (sc) => {
        setOpenDuplicate(true)
        setDuplicateScenario({
            ...sc,
            name: `${sc?.name || ''} (${i18n.copy})`,
            id: undefined,
        })
    }

    useActions(() => {
        if (isTrue(accountUser.isAdmin) || isTrue(accountUser.metadata)) {
            return editMode ? {
                cancel: () => {
                    setLocalScenario(scenario)
                    setEditMode(false)
                },
                save: () => {
                    dispatch(AgriAction.updateScenario({ ...localScenario, linkRules: [...localScenario.linkRules.map((lR) => ({ ...lR, parameters: JSON.stringify(lR.parameters) }))] })).then((res) => {
                        if (res) {
                            setLocalDatasRules(res.datasScenario || [])
                            setLocalDatasDiffRules(res.datasDiffScenario || [])
                        }
                    })
                    setEditMode(false)
                },
            } : {
                delete: () => dispatch(AgriAction.deleteScenario(idScenario)).then(() => dispatch(push(`/par/${localScenario.idPAR}`))),
                edit: () => setEditMode(true),
                duplicate: () => duplicateAction(localScenario),
                other: {
                    other: (
                        <Other
                            className='clickable'
                            tooltip={i18n.pdfFile}
                            icon='file_download'
                            onClick={toPDF}
                        />
                    ),
                },
            }
        }
        return {}
    }, [editMode, scenario, localScenario])

    const onCheckRule = (active, idRule) => {
        const othersRules = (localScenario.linkRules || []).filter((lr) => lr.idRule !== idRule)
        const othersRulesBis = idRule === -2 ? othersRules.filter((lr) => ![-3, -4].includes(lr.idRule)) : othersRules
        const othersRulesTer = [-3, -4].includes(idRule) ? othersRulesBis.filter((lr) => lr.idRule !== -2) : othersRulesBis
        setLocalScenario({
            ...localScenario,
            linkRules: [
                ...othersRulesTer,
                {
                    idScenario: scenario.id,
                    idRule,
                    active,
                    parameters: (localScenario.linkRules || []).find((lr) => lr.idRule === idRule)?.parameters || {},
                },
            ],
        })
    }

    const [ugeVolumes, aupVolumes] = useMemo(() => {
        const aupFound = orderBy((uge.link_aup || []).filter((aup) => aup.year <= PAR.year), 'year', 'desc')[0] || {}
        const volumesFound = PAR.linkVolumesUGE.find((u) => u.subManagementCode === scenario.managementCode) || PAR.linkVolumesUGE.find((u) => u.managementCode === scenario.managementCode) || {}
        return [volumesFound, aupFound]
    }, [PAR, scenario, uge])

    const [volumeAskedESU, volumeAskedLowWaterESU, volumeAskedNotLowWaterESU] = useMemo(() => {
        const datasESU = localDatasRules.filter((d) => d.typePrel === 2)
        return [
            round(datasESU.reduce((acc, d) => acc + d.volumeAsked, 0)),
            round(datasESU.reduce((acc, d) => acc + d.volumeAskedLowWater, 0)),
            round(datasESU.reduce((acc, d) => acc + d.volumeAskedNotLowWater, 0)),
        ]
    }, [localDatasRules])

    const [
        totalSkimmingESU, lowWaterSkimmingESU, notLowWaterSkimmingESU,
        totalArbitrationESU, lowWaterArbitrationESU, notLowWaterArbitrationESU,
        totalExceptionsESU, lowWaterExceptionsESU, notLowWaterExceptionsESU,
    ] = useMemo(() => {
        const esuKeys = keyBy(localDatasRules.filter((d) => d.typePrel === 2), (d) => `${d.idExploitation}|${d.idInstallation}`)
        const esuDiffs = localDatasDiffRules.filter((d) => !!esuKeys[`${d.idExploitation}|${d.idInstallation}`])
        const getActiveRulesDiffs = (ruleType) => rulesScenarios
            .filter((r) => r.ruleType === ruleType && (localScenario.linkRules || []).some((lR) => lR.idRule === r.id && lR.active))
            .flatMap((r) => esuDiffs.filter((d) => d.idRule === r.id))
        const arbitrationDiffs = getActiveRulesDiffs('arbitrage')
        const exceptionsDiffs = arbitrationDiffs.filter((d) => d.exempt || (localScenario.linkRules || []).find((lR) => lR.idRule === d.idRule)?.parameters?.exceptions
            ?.some((e) => e.idExploitation === d.idExploitation && e.idInstallation === d.idInstallation))
        const sum = (diffs, key) => round(diffs.reduce((acc, d) => acc + d[key], 0))
        const sumAbs = (diffs, key) => round(diffs.reduce((acc, d) => acc + Math.abs(d[key]), 0))
        const skimmingDiffs = getActiveRulesDiffs('ecremage')
        return [
            sum(skimmingDiffs, 'volumeDiff'),
            sum(skimmingDiffs, 'volumeDiffLowWater'),
            sum(skimmingDiffs, 'volumeDiffNotlowWater'),
            sum(arbitrationDiffs, 'volumeDiff'),
            sum(arbitrationDiffs, 'volumeDiffLowWater'),
            sum(arbitrationDiffs, 'volumeDiffNotlowWater'),
            sumAbs(exceptionsDiffs, 'volumeDiff'),
            sumAbs(exceptionsDiffs, 'volumeDiffLowWater'),
            sumAbs(exceptionsDiffs, 'volumeDiffNotlowWater'),
        ]
    }, [localDatasRules, localDatasDiffRules, rulesScenarios, localScenario.linkRules])

    const [rulesSkimming, totalSkimming, lowWaterSkimming, notLowWaterSkimming] = useMemo(() => {
        const rules = rulesScenarios.filter((r) => r.ruleType === 'ecremage')
        let total = 0
        let lowWater = 0
        let notLowWater = 0
        const datas = rules.map((r) => {
            const isActive = localScenario?.linkRules?.some((lR) => lR.idRule === r.id && lR.active)
            const diffs = localDatasDiffRules.filter((d) => d.idRule === r.id)
            const impactedVolume = round(diffs.reduce((acc, d) => acc + d.volumeDiff, 0))
            const impactedLowWaterVolume = round(diffs.reduce((acc, d) => acc + d.volumeDiffLowWater, 0))
            const impactedNotLowWaterVolume = round(diffs.reduce((acc, d) => acc + d.volumeDiffNotlowWater, 0))
            total = total + (isActive ? impactedVolume : 0)
            lowWater = lowWater + (isActive ? impactedLowWaterVolume : 0)
            notLowWater = notLowWater + (isActive ? impactedNotLowWaterVolume : 0)
            return {
                ...r,
                ruleName: r.label,
                nullValue: <Checkbox
                    col
                    checked={localScenario?.linkRules?.some((lr) => lr.idRule === r.id && lr.active)}
                    onChange={v => onCheckRule(v, r.id)}
                    disabled={!editMode}
                />,
                totalVolume: impactedVolume === 0 ? 0 : `-${formatMilliers(impactedVolume)}`,
                impactedLowWaterVolume: impactedLowWaterVolume === 0 ? 0 : `-${formatMilliers(impactedLowWaterVolume)}`,
                impactedNotLowWaterVolume: impactedNotLowWaterVolume === 0 ? 0 : `-${formatMilliers(impactedNotLowWaterVolume)}`,
                percentOfAUP: `${formatMilliers(round((impactedVolume * 100) / aupVolumes.aup, 0))}%`,
                nullValue2: !r.description ? '' : <Tooltip title={r.description} placement='left'><Info /></Tooltip>,
            }
        })
        return [datas, total, lowWater, notLowWater]
    }, [rulesScenarios, editMode, localScenario.linkRules, localDatasDiffRules, aupVolumes.aup])

    const [rulesArbitration, totalArbitration, lowWaterArbitration, notLowWaterArbitration, totalExceptions, lowWaterExceptions, notLowWaterExceptions] = useMemo(() => {
        const rules = rulesScenarios.filter((r) => r.ruleType === 'arbitrage')
        let total = 0
        let lowWater = 0
        let notLowWater = 0
        let totalExceptionsTmp = 0
        let lowWaterExceptionsTmp = 0
        let notLowWaterExceptionsTmp = 0
        const datas = orderBy(rules.map((r) => {
            const linkRule = localScenario?.linkRules?.find((lR) => lR.idRule === r.id) || {}
            const { active, parameters } = linkRule
            const diffs = localDatasDiffRules.filter((d) => d.idRule === r.id)
            const impactedVolume = round(diffs.reduce((acc, d) => acc + d.volumeDiff, 0))
            const impactedLowWaterVolume = round(diffs.reduce((acc, d) => acc + d.volumeDiffLowWater, 0))
            const impactedNotLowWaterVolume = round(diffs.reduce((acc, d) => acc + d.volumeDiffNotlowWater, 0))
            total = total + (active ? impactedVolume : 0)
            lowWater = lowWater + (active ? impactedLowWaterVolume : 0)
            notLowWater = notLowWater + (active ? impactedNotLowWaterVolume : 0)
            const diffsExceptions = localDatasDiffRules.filter((d) => d.idRule === r.id && (d.exempt || parameters?.exceptions?.some((e) => e.idExploitation === d.idExploitation && e.idInstallation === d.idInstallation)))
            const exceptionsVolume = round(diffsExceptions.reduce((acc, d) => acc + Math.abs(d.volumeDiff), 0))
            const exceptionsLowWaterVolume = round(diffsExceptions.reduce((acc, d) => acc + Math.abs(d.volumeDiffLowWater), 0))
            const exceptionsNotLowWaterVolume = round(diffsExceptions.reduce((acc, d) => acc + Math.abs(d.volumeDiffNotlowWater), 0))
            totalExceptionsTmp = totalExceptionsTmp + (active ? exceptionsVolume : 0)
            lowWaterExceptionsTmp = lowWaterExceptionsTmp + (active ? exceptionsLowWaterVolume : 0)
            notLowWaterExceptionsTmp = notLowWaterExceptionsTmp + (active ? exceptionsNotLowWaterVolume : 0)
            return {
                ...r,
                displayLoadingRow: !dataLoaded && rule.id === r.id,
                ruleName: r.label,
                nullValue: <Checkbox
                    col
                    checked={localScenario?.linkRules?.some((lr) => lr.idRule === r.id && lr.active)}
                    onChange={v => onCheckRule(v, r.id)}
                    disabled={!editMode}
                />,
                totalVolume: formatMilliers(impactedVolume),
                impactedLowWaterVolume: formatMilliers(impactedLowWaterVolume),
                impactedNotLowWaterVolume: formatMilliers(impactedNotLowWaterVolume),
                volumeExemptFromRule: formatMilliers(exceptionsVolume),
                percentOfAUP: `${formatMilliers(round((Math.abs(impactedVolume) * 100) / aupVolumes.aup, 0))}%`,
                nullValue2: !r.description ? '' : <Tooltip title={r.description} placement='left'><Info /></Tooltip>,
            }
        }), 'id')
        return [datas, total, lowWater, notLowWater, totalExceptionsTmp, lowWaterExceptionsTmp, notLowWaterExceptionsTmp]
    }, [rulesScenarios, editMode, localScenario.linkRules, localDatasDiffRules, aupVolumes.aup])

    const onCreateScenario = () => {
        if (duplicateScenario.name) {
            dispatch(AgriAction.createMultipleScenarios([{
                ...duplicateScenario,
                idPAR: scenario.idPAR,
                linkRules: [...localScenario.linkRules.map((lR) => ({ ...lR, parameters: JSON.stringify(lR.parameters) }))],
            }])).then((ids) => {
                dispatch(AgriAction.fetchPAR(scenario.idPAR))
                dispatch(push(`/scenario/${ids[0]}`))
            })
        } else {
            dispatch(ToastrAction.error(i18n.pleaseCompleteAllRequiredField))
        }
    }

    const onSaveRule = () => {
        const { exceptions } = rule.parameters
        setDataLoaded(false)
        if (rule.id) {
            dispatch(AgriAction.updateRuleScenarios({ ...rule, nullValue: undefined, nullValue2: undefined, parameters: JSON.stringify({ ...rule.parameters, exceptions: undefined }) })).then(() => {
                dispatch(AgriAction.fetchRulesScenarios())
                dispatch(AgriAction.fetchScenario(idScenario)).then((res) => {
                    setLocalScenario({
                        ...res.scenario,
                        linkRules: [
                            ...(res.scenario.linkRules || []).filter((lr) => lr.idRule !== rule.id),
                            {
                                idScenario: scenario.id,
                                idRule: rule.id,
                                active: (res.scenario.linkRules || []).find((lr) => lr.idRule === rule.id)?.active,
                                parameters: { exceptions },
                            },
                        ],
                    })
                    setDataLoaded(true)
                    setLocalDatasRules(res?.datasScenario || [])
                    setLocalDatasDiffRules(res?.datasDiffScenario || [])
                })
            })
        } else {
            dispatch(AgriAction.createRuleScenarios({ ...rule, nullValue: undefined, nullValue2: undefined, parameters: JSON.stringify({ ...rule.parameters, exceptions: undefined }) })).then((idRule) => {
                dispatch(AgriAction.fetchRulesScenarios())
                dispatch(AgriAction.fetchScenario(idScenario)).then((res) => {
                    setLocalScenario({
                        ...res.scenario,
                        linkRules: [
                            ...res.scenario.linkRules,
                            {
                                idScenario: scenario.id,
                                idRule,
                                active: true,
                                parameters: { exceptions },
                            },
                        ],
                    })
                    setDataLoaded(true)
                    setLocalDatasRules(res?.datasScenario || [])
                    setLocalDatasDiffRules(res?.datasDiffScenario || [])
                })
            })
        }
    }

    const getDialogScenario = () => (
        <Dialog
            fullWidth
            open={openDuplicate}
            maxWidth='md'
        >
            <DefaultDialogTitle title={i18n.createNewScenario} onClose={() => setOpenDuplicate(false)} />
            <DialogContent>
                <Grid container>
                    <Grid size={12}>
                        <Input
                            title={i18n.name}
                            value={duplicateScenario.name}
                            onChange={value => setDuplicateScenario({ ...duplicateScenario, name: value })}
                            obligatory
                        />
                    </Grid>
                    <Grid size={12}>
                        <Textarea
                            title={i18n.description}
                            value={duplicateScenario.description}
                            onChange={value => setDuplicateScenario({ ...duplicateScenario, description: value })}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCreateScenario} variant='contained' color='primary'>
                    {i18n.create}
                </Button>
            </DialogActions>
        </Dialog>
    )

    const [
        totalAllocated, lowWaterAllocated, notLowWaterAllocated,
        totalAllocatedESU, lowWaterAllocatedESU, notLowWaterAllocatedESU,
    ] = useMemo(() => {
        return [
            (ugeVolumes.volume - totalSkimming + totalArbitration + totalExceptions),
            (ugeVolumes.volumeAskedLowWater - lowWaterSkimming + lowWaterArbitration + lowWaterExceptions),
            (ugeVolumes.volumeAskedNotLowWater - notLowWaterSkimming + notLowWaterArbitration + notLowWaterExceptions),
            (volumeAskedESU - totalSkimmingESU + totalArbitrationESU + totalExceptionsESU),
            (volumeAskedLowWaterESU - lowWaterSkimmingESU + lowWaterArbitrationESU + lowWaterExceptionsESU),
            (volumeAskedNotLowWaterESU - notLowWaterSkimmingESU + notLowWaterArbitrationESU + notLowWaterExceptionsESU),
        ]
    }, [lowWaterArbitration, lowWaterSkimming, notLowWaterArbitration, notLowWaterSkimming, totalArbitration, totalSkimming, ugeVolumes,
        totalExceptions, lowWaterExceptions, notLowWaterExceptions,
        volumeAskedESU, volumeAskedLowWaterESU, volumeAskedNotLowWaterESU,
        totalSkimmingESU, lowWaterSkimmingESU, notLowWaterSkimmingESU,
        totalArbitrationESU, lowWaterArbitrationESU, notLowWaterArbitrationESU,
        totalExceptionsESU, lowWaterExceptionsESU, notLowWaterExceptionsESU])

    const optionsChart = useMemo(() => {
        const lowWaterVolumes = {
            asked: ugeVolumes.volumeAskedLowWater,
            skimming: lowWaterSkimming,
            arbitration: lowWaterArbitration,
            exception: lowWaterExceptions,
            allocated: lowWaterAllocated,
            aup: aupVolumes.aupLowWater,
        }
        const notLowWaterVolumes = {
            asked: ugeVolumes.volumeAskedNotLowWater,
            skimming: notLowWaterSkimming,
            arbitration: notLowWaterArbitration,
            exception: notLowWaterExceptions,
            allocated: notLowWaterAllocated,
            aup: aupVolumes.aupExceptLowWater,
        }
        const esupLowWaterVolumes = {
            asked: volumeAskedLowWaterESU,
            skimming: lowWaterSkimmingESU,
            arbitration: lowWaterArbitrationESU,
            exception: lowWaterExceptionsESU,
            allocated: lowWaterAllocatedESU,
            aup: aupVolumes.aupLowWaterSup,
        }
        const esupNotLowWaterVolumes = {
            asked: volumeAskedNotLowWaterESU,
            skimming: notLowWaterSkimmingESU,
            arbitration: notLowWaterArbitrationESU,
            exception: notLowWaterExceptionsESU,
            allocated: notLowWaterAllocatedESU,
            aup: aupVolumes.aupExceptLowWaterSup,
        }
        const getEsoutVolumes = (volumes, esupVolumes, aup) => ({
            asked: volumes.asked - esupVolumes.asked,
            skimming: volumes.skimming - esupVolumes.skimming,
            arbitration: volumes.arbitration - esupVolumes.arbitration,
            exception: volumes.exception - esupVolumes.exception,
            allocated: volumes.allocated - esupVolumes.allocated,
            aup,
        })

        const esupNames = [`${i18n.superficial} - ${i18n.lowWater}`, `${i18n.superficial} - ${i18n.notLowWater}`]
        const esoutNames = [`${i18n.underground} - ${i18n.lowWater}`, `${i18n.underground} - ${i18n.notLowWater}`]

        const series = [
            getWaterfallBaseSeries({ name: i18n.lowWater, color: colorLowWater, volumes: lowWaterVolumes }),
            getWaterfallBaseSeries({ name: i18n.notLowWater, color: colorNotLowWater, volumes: notLowWaterVolumes }),
            ...(activateEsupEsoutDistinction ? [
                getWaterfallBarSeries({ name: esupNames[0], color: colorLowWater, volumes: esupLowWaterVolumes }),
                getWaterfallBarSeries({ name: esupNames[1], color: colorNotLowWater, volumes: esupNotLowWaterVolumes }),
                getWaterfallBarSeries({ name: esoutNames[0], color: secondaryBlue, volumes: getEsoutVolumes(lowWaterVolumes, esupLowWaterVolumes, aupVolumes.aupLowWaterSout) }),
                getWaterfallBarSeries({ name: esoutNames[1], color: mainBlue, volumes: getEsoutVolumes(notLowWaterVolumes, esupNotLowWaterVolumes, aupVolumes.aupExceptLowWaterSout) }),
            ] : [
                getWaterfallBarSeries({ name: i18n.lowWater, color: colorLowWater, volumes: lowWaterVolumes }),
                getWaterfallBarSeries({ name: i18n.notLowWater, color: colorNotLowWater, volumes: notLowWaterVolumes }),
            ]),
            Line({
                markLine: {
                    symbol: 'none',
                    label: { show: false },
                    data: [
                        { yAxis: ugeVolumes?.volume ?? 0 },
                        { yAxis: ugeVolumes?.volume - totalSkimming ?? 0 },
                        { yAxis: ugeVolumes?.volume - totalSkimming - Math.abs(totalArbitration) ?? 0 },
                        { yAxis: totalAllocated ?? 0 },
                        { yAxis: aupVolumes?.aup ?? 0 },
                    ],
                },
            }),
        ]

        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow',
                },
                formatter: (params) => {
                    const bars = params.filter((p) => p.data?.itemStyle?.color !== 'transparent')
                    return params[0]?.name + bars.map((p) => `<br/>${p.seriesName} : ${formatMilliers(p.value)} m3`).reduce((acc, p) => acc + p, '')
                },
            },
            legend: {
                data: activateEsupEsoutDistinction ? [...esupNames, ...esoutNames] : [i18n.lowWater, i18n.notLowWater],
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '3%',
                top: '10%',
                containLabel: true,
            },
            xAxis: [Axis({
                type: 'category',
                splitLine: { show: false },
                data: [i18n.asked, i18n.skimmed, i18n.arbitrated, i18n.exception, i18n.allocated, i18n.reserve, i18n.AUP],
            })],
            yAxis: [Axis({ type: 'value' })],
            series,
        }
    }, [activateEsupEsoutDistinction, aupVolumes.aupExceptLowWater, aupVolumes.aupLowWater, lowWaterAllocated, lowWaterArbitration, lowWaterExceptions, lowWaterSkimming,
        notLowWaterAllocated, notLowWaterArbitration, notLowWaterExceptions, notLowWaterSkimming, ugeVolumes.volumeAskedLowWater,
        ugeVolumes.volumeAskedNotLowWater, aupVolumes.aup, totalAllocated, totalSkimming, totalArbitration, ugeVolumes.volume,
        volumeAskedLowWaterESU, volumeAskedNotLowWaterESU, lowWaterAllocatedESU, notLowWaterAllocatedESU,
        lowWaterSkimmingESU, notLowWaterSkimmingESU, lowWaterArbitrationESU, notLowWaterArbitrationESU, lowWaterExceptionsESU, notLowWaterExceptionsESU,
        aupVolumes.aupLowWaterSup, aupVolumes.aupExceptLowWaterSup, aupVolumes.aupLowWaterSout, aupVolumes.aupExceptLowWaterSout])

    const getTooltipVolumesTypePrel = (volumeESU, volumeESO) => (
        <>
            <span>{`${i18n.superficial} : ${isNil(volumeESU) || isNaN(volumeESU) ? i18n.unknown : `${formatMilliers(volumeESU)} m3`}`}</span><br />
            <span>{`${i18n.underground} : ${isNil(volumeESO) || isNaN(volumeESO) ? i18n.unknown : `${formatMilliers(volumeESO)} m3`}`}</span>
        </>
    )

    const totalSkimmingESO = totalSkimming - totalSkimmingESU
    const lowWaterSkimmingESO = lowWaterSkimming - lowWaterSkimmingESU
    const notLowWaterSkimmingESO = notLowWaterSkimming - notLowWaterSkimmingESU
    const totalArbitrationESO = totalArbitration - totalArbitrationESU
    const lowWaterArbitrationESO = lowWaterArbitration - lowWaterArbitrationESU
    const notLowWaterArbitrationESO = notLowWaterArbitration - notLowWaterArbitrationESU
    const totalExceptionsESO = totalExceptions - totalExceptionsESU
    const lowWaterExceptionsESO = lowWaterExceptions - lowWaterExceptionsESU
    const notLowWaterExceptionsESO = notLowWaterExceptions - notLowWaterExceptionsESU

    const overviewPanel = !activateEsupEsoutDistinction ?
        (<CardTable
            title={i18n.overview}
            rows={[{
                nullValue: { value: i18n.requestedVolume },
                lowWater: { value: formatMilliers(ugeVolumes.volumeAskedLowWater), tooltip: getTooltipVolumesTypePrel(volumeAskedLowWaterESU, ugeVolumes.volumeAskedLowWater - volumeAskedLowWaterESU) },
                notLowWater: { value: formatMilliers(ugeVolumes.volumeAskedNotLowWater), tooltip: getTooltipVolumesTypePrel(volumeAskedNotLowWaterESU, ugeVolumes.volumeAskedNotLowWater - volumeAskedNotLowWaterESU) },
                totalVolume: { value: formatMilliers(ugeVolumes.volume), tooltip: getTooltipVolumesTypePrel(volumeAskedESU, ugeVolumes.volume - volumeAskedESU) },
                deviationFromAUP: { value: !isUndefined(ugeVolumes.volume) && !isUndefined(aupVolumes.aup) ? `${round(((ugeVolumes.volume - aupVolumes.aup) / aupVolumes.aup) * 100)}%` : '-' },
            }, {
                nullValue: { value: i18n.skimming },
                lowWater: { value: lowWaterSkimming === 0 ? 0 : `-${formatMilliers(lowWaterSkimming)}`, tooltip: getTooltipVolumesTypePrel(-lowWaterSkimmingESU, -lowWaterSkimmingESO) },
                notLowWater: { value: notLowWaterSkimming === 0 ? 0 : `-${formatMilliers(notLowWaterSkimming)}`, tooltip: getTooltipVolumesTypePrel(-notLowWaterSkimmingESU, -notLowWaterSkimmingESO) },
                totalVolume: { value: totalSkimming === 0 ? 0 : `-${formatMilliers(totalSkimming)}`, tooltip: getTooltipVolumesTypePrel(-totalSkimmingESU, -totalSkimmingESO) },
            }, {
                nullValue: { value: i18n.arbitration },
                lowWater: { value: formatMilliers(lowWaterArbitration), tooltip: getTooltipVolumesTypePrel(lowWaterArbitrationESU, lowWaterArbitrationESO) },
                notLowWater: { value: formatMilliers(notLowWaterArbitration), tooltip: getTooltipVolumesTypePrel(notLowWaterArbitrationESU, notLowWaterArbitrationESO) },
                totalVolume: { value: formatMilliers(totalArbitration), tooltip: getTooltipVolumesTypePrel(totalArbitrationESU, totalArbitrationESO) },
            }, {
                nullValue: { value: i18n.exception },
                lowWater: { value: formatMilliers(lowWaterExceptions), tooltip: getTooltipVolumesTypePrel(lowWaterExceptionsESU, lowWaterExceptionsESO) },
                notLowWater: { value: formatMilliers(notLowWaterExceptions), tooltip: getTooltipVolumesTypePrel(notLowWaterExceptionsESU, notLowWaterExceptionsESO) },
                totalVolume: { value: formatMilliers(totalExceptions), tooltip: getTooltipVolumesTypePrel(totalExceptionsESU, totalExceptionsESO) },
            }, {
                nullValue: { value: i18n.allocatedVolume },
                lowWater: { value: formatMilliers(lowWaterAllocated), tooltip: getTooltipVolumesTypePrel(lowWaterAllocatedESU, lowWaterAllocated - lowWaterAllocatedESU) },
                notLowWater: { value: formatMilliers(notLowWaterAllocated), tooltip: getTooltipVolumesTypePrel(notLowWaterAllocatedESU, notLowWaterAllocated - notLowWaterAllocatedESU) },
                totalVolume: { value: formatMilliers(totalAllocated), tooltip: getTooltipVolumesTypePrel(totalAllocatedESU, totalAllocated - totalAllocatedESU) },
                deviationFromAUP: { value: !isUndefined(totalAllocated) && !isUndefined(aupVolumes.aup) ? `${round(((totalAllocated - aupVolumes.aup) / aupVolumes.aup) * 100)}%` : '-' },
            }, {
                nullValue: { value: i18n.reserveVolume },
                lowWater: { value: formatMilliers(aupVolumes.aupLowWater - lowWaterAllocated), tooltip: getTooltipVolumesTypePrel(aupVolumes.aupLowWaterSup - lowWaterAllocatedESU, aupVolumes.aupLowWaterSout - (lowWaterAllocated - lowWaterAllocatedESU)) },
                notLowWater: { value: formatMilliers(aupVolumes.aupExceptLowWater - notLowWaterAllocated), tooltip: getTooltipVolumesTypePrel(aupVolumes.aupExceptLowWaterSup - notLowWaterAllocatedESU, aupVolumes.aupExceptLowWaterSout - (notLowWaterAllocated - notLowWaterAllocatedESU)) },
                totalVolume: { value: formatMilliers(aupVolumes.aup - totalAllocated), tooltip: getTooltipVolumesTypePrel(aupVolumes.aupLowWaterSup + aupVolumes.aupExceptLowWaterSup - totalAllocatedESU, aupVolumes.aupLowWaterSout + aupVolumes.aupExceptLowWaterSout - (totalAllocated - totalAllocatedESU)) },
                deviationFromAUP: { value: !isUndefined(totalAllocated) && !isUndefined(aupVolumes.aup) ? `${0 - round(((totalAllocated) / aupVolumes.aup) * 100)}%` : '-' },
            }, {
                nullValue: { value: i18n.AUPVolume },
                lowWater: { value: formatMilliers(aupVolumes.aupLowWater), tooltip: getTooltipVolumesTypePrel(aupVolumes.aupLowWaterSup, aupVolumes.aupLowWaterSout) },
                notLowWater: { value: formatMilliers(aupVolumes.aupExceptLowWater), tooltip: getTooltipVolumesTypePrel(aupVolumes.aupExceptLowWaterSup, aupVolumes.aupExceptLowWaterSout) },
                totalVolume: { value: formatMilliers(aupVolumes.aup), tooltip: getTooltipVolumesTypePrel(aupVolumes.aupLowWaterSup + aupVolumes.aupExceptLowWaterSup, aupVolumes.aupLowWaterSout + aupVolumes.aupExceptLowWaterSout) },
                deviationFromAUP: { value: '-' },
            }]}
            headers={[{ key: 'nullValue', value: '' }, 'lowWater', 'notLowWater', 'totalVolume', 'deviationFromAUP']}
            color={darkBlue}
        />)
        :
        (<Stack spacing={1}>
            <CardTable
                title={i18n.esupOverview}
                rows={[{
                    nullValue: { value: i18n.requestedVolume },
                    lowWater: { value: formatMilliers(volumeAskedLowWaterESU) },
                    notLowWater: { value: formatMilliers(volumeAskedNotLowWaterESU) },
                    totalVolume: { value: formatMilliers(volumeAskedESU) },
                    deviationFromAUP: { value: !isUndefined(volumeAskedESU) && !isUndefined(aupVolumes.aupLowWaterSup) && !isUndefined(aupVolumes.aupExceptLowWaterSup) ? `${round(((volumeAskedESU - (aupVolumes.aupLowWaterSup + aupVolumes.aupExceptLowWaterSup)) / (aupVolumes.aupLowWaterSup + aupVolumes.aupExceptLowWaterSup)) * 100)}%` : '-' },
                }, {
                    nullValue: { value: i18n.skimming },
                    lowWater: { value: lowWaterSkimmingESU === 0 ? 0 : `-${formatMilliers(lowWaterSkimmingESU)}` },
                    notLowWater: { value: notLowWaterSkimmingESU === 0 ? 0 : `-${formatMilliers(notLowWaterSkimmingESU)}` },
                    totalVolume: { value: totalSkimmingESU === 0 ? 0 : `-${formatMilliers(totalSkimmingESU)}` },
                }, {
                    nullValue: { value: i18n.arbitration },
                    lowWater: { value: formatMilliers(lowWaterArbitrationESU) },
                    notLowWater: { value: formatMilliers(notLowWaterArbitrationESU) },
                    totalVolume: { value: formatMilliers(totalArbitrationESU) },
                }, {
                    nullValue: { value: i18n.exception },
                    lowWater: { value: formatMilliers(lowWaterExceptionsESU) },
                    notLowWater: { value: formatMilliers(notLowWaterExceptionsESU) },
                    totalVolume: { value: formatMilliers(totalExceptionsESU) },
                }, {
                    nullValue: { value: i18n.allocatedVolume },
                    lowWater: { value: formatMilliers(lowWaterAllocatedESU) },
                    notLowWater: { value: formatMilliers(notLowWaterAllocatedESU) },
                    totalVolume: { value: formatMilliers(totalAllocatedESU) },
                    deviationFromAUP: { value: !isUndefined(totalAllocatedESU) && !isUndefined(aupVolumes.aupLowWaterSup) && !isUndefined(aupVolumes.aupExceptLowWaterSup) ? `${round(((totalAllocatedESU - (aupVolumes.aupLowWaterSup + aupVolumes.aupExceptLowWaterSup)) / (aupVolumes.aupLowWaterSup + aupVolumes.aupExceptLowWaterSup)) * 100)}%` : '-' },
                }, {
                    nullValue: { value: i18n.reserveVolume },
                    lowWater: { value: formatMilliers(aupVolumes.aupLowWaterSup - lowWaterAllocatedESU) },
                    notLowWater: { value: formatMilliers(aupVolumes.aupExceptLowWaterSup - notLowWaterAllocatedESU) },
                    totalVolume: { value: formatMilliers(aupVolumes.aupLowWaterSup + aupVolumes.aupExceptLowWaterSup - totalAllocatedESU) },
                    deviationFromAUP: { value: !isUndefined(totalAllocatedESU) && !isUndefined(aupVolumes.aupLowWaterSup) && !isUndefined(aupVolumes.aupExceptLowWaterSup) ? `${0 - round((totalAllocatedESU / (aupVolumes.aupLowWaterSup + aupVolumes.aupExceptLowWaterSup)) * 100)}%` : '-' },
                }, {
                    nullValue: { value: i18n.AUPVolume },
                    lowWater: { value: formatMilliers(aupVolumes.aupLowWaterSup) },
                    notLowWater: { value: formatMilliers(aupVolumes.aupExceptLowWaterSup) },
                    totalVolume: { value: formatMilliers(aupVolumes.aupLowWaterSup + aupVolumes.aupExceptLowWaterSup) },
                    deviationFromAUP: { value: '-' },
                }]}
                headers={[{ key: 'nullValue', value: '' }, 'lowWater', 'notLowWater', 'totalVolume', 'deviationFromAUP']}
                color={darkBlue}
            />
            <CardTable
                title={i18n.esoutOverview}
                rows={[{
                    nullValue: { value: i18n.requestedVolume },
                    lowWater: { value: formatMilliers(ugeVolumes.volumeAskedLowWater - volumeAskedLowWaterESU) },
                    notLowWater: { value: formatMilliers(ugeVolumes.volumeAskedNotLowWater - volumeAskedNotLowWaterESU) },
                    totalVolume: { value: formatMilliers(ugeVolumes.volume - volumeAskedESU) },
                    deviationFromAUP: { value: !isUndefined(ugeVolumes.volume) && !isUndefined(volumeAskedESU) && !isUndefined(aupVolumes.aupLowWaterSout) && !isUndefined(aupVolumes.aupExceptLowWaterSout) ? `${round(((ugeVolumes.volume - volumeAskedESU - (aupVolumes.aupLowWaterSout + aupVolumes.aupExceptLowWaterSout)) / (aupVolumes.aupLowWaterSout + aupVolumes.aupExceptLowWaterSout)) * 100)}%` : '-' },
                }, {
                    nullValue: { value: i18n.skimming },
                    lowWater: { value: lowWaterSkimmingESO === 0 ? 0 : `-${formatMilliers(lowWaterSkimmingESO)}` },
                    notLowWater: { value: notLowWaterSkimmingESO === 0 ? 0 : `-${formatMilliers(notLowWaterSkimmingESO)}` },
                    totalVolume: { value: totalSkimmingESO === 0 ? 0 : `-${formatMilliers(totalSkimmingESO)}` },
                }, {
                    nullValue: { value: i18n.arbitration },
                    lowWater: { value: formatMilliers(lowWaterArbitrationESO) },
                    notLowWater: { value: formatMilliers(notLowWaterArbitrationESO) },
                    totalVolume: { value: formatMilliers(totalArbitrationESO) },
                }, {
                    nullValue: { value: i18n.exception },
                    lowWater: { value: formatMilliers(lowWaterExceptionsESO) },
                    notLowWater: { value: formatMilliers(notLowWaterExceptionsESO) },
                    totalVolume: { value: formatMilliers(totalExceptionsESO) },
                }, {
                    nullValue: { value: i18n.allocatedVolume },
                    lowWater: { value: formatMilliers(lowWaterAllocated - lowWaterAllocatedESU) },
                    notLowWater: { value: formatMilliers(notLowWaterAllocated - notLowWaterAllocatedESU) },
                    totalVolume: { value: formatMilliers(totalAllocated - totalAllocatedESU) },
                    deviationFromAUP: { value: !isUndefined(totalAllocated) && !isUndefined(totalAllocatedESU) && !isUndefined(aupVolumes.aupLowWaterSout) && !isUndefined(aupVolumes.aupExceptLowWaterSout) ? `${round(((totalAllocated - totalAllocatedESU - (aupVolumes.aupLowWaterSout + aupVolumes.aupExceptLowWaterSout)) / (aupVolumes.aupLowWaterSout + aupVolumes.aupExceptLowWaterSout)) * 100)}%` : '-' },
                }, {
                    nullValue: { value: i18n.reserveVolume },
                    lowWater: { value: formatMilliers(aupVolumes.aupLowWaterSout - (lowWaterAllocated - lowWaterAllocatedESU)) },
                    notLowWater: { value: formatMilliers(aupVolumes.aupExceptLowWaterSout - (notLowWaterAllocated - notLowWaterAllocatedESU)) },
                    totalVolume: { value: formatMilliers(aupVolumes.aupLowWaterSout + aupVolumes.aupExceptLowWaterSout - (totalAllocated - totalAllocatedESU)) },
                    deviationFromAUP: { value: !isUndefined(totalAllocated) && !isUndefined(totalAllocatedESU) && !isUndefined(aupVolumes.aupLowWaterSout) && !isUndefined(aupVolumes.aupExceptLowWaterSout) ? `${0 - round(((totalAllocated - totalAllocatedESU) / (aupVolumes.aupLowWaterSout + aupVolumes.aupExceptLowWaterSout)) * 100)}%` : '-' },
                }, {
                    nullValue: { value: i18n.AUPVolume },
                    lowWater: { value: formatMilliers(aupVolumes.aupLowWaterSout) },
                    notLowWater: { value: formatMilliers(aupVolumes.aupExceptLowWaterSout) },
                    totalVolume: { value: formatMilliers(aupVolumes.aupLowWaterSout + aupVolumes.aupExceptLowWaterSout) },
                    deviationFromAUP: { value: '-' },
                }]}
                headers={[{ key: 'nullValue', value: '' }, 'lowWater', 'notLowWater', 'totalVolume', 'deviationFromAUP']}
                color={darkBlue}
            />
        </Stack>)

    if (scenario.id !== idScenario) return <ProgressCard indeterminate round />

    return (
        <Stack spacing={1} ref={targetRef} sx={{ padding: '5px 0 80px 0' }}>
            <Card>
                <CardTitle title={i18n.generalInfos} color={darkBlue} />
                <CardContent>
                    <Grid container spacing={1}>
                        <Grid container size={6}>
                            <Grid size={6}>
                                <SimpleInput
                                    title={i18n.name}
                                    value={localScenario.name}
                                    onChange={value => setLocalScenario({ ...localScenario, name: value })}
                                    disabled={!editMode}
                                />
                            </Grid>
                            <Grid size={6}>
                                <SimpleSelect
                                    label={i18n.status}
                                    options={getSandreList(sandreCodes, 'AGRI_SCENARIO.STATUT')}
                                    value={localScenario.statusCode}
                                    keyValue='code'
                                    onChange={value => setLocalScenario({ ...localScenario, statusCode: value })}
                                    disabled={!editMode}
                                />
                            </Grid>
                            {activateRegulationCoef && <Grid size={6}>
                                <SimpleInput
                                    title={i18n.regulationCoef}
                                    disabled={!editMode}
                                    type={INPUT_TYPES.float}
                                    min={0}
                                    value={localScenario.regulationCoef}
                                    onChange={newCoef => setLocalScenario(prev => ({ ...prev, regulationCoef: newCoef }))}
                                />
                            </Grid>}
                            <Grid size={6}>
                                <SimpleCheckbox
                                    label={i18n.esupEsoutDistinction}
                                    checked={activateEsupEsoutDistinction}
                                    onToggle={() => setLocalScenario(prev => ({ ...prev, esupEsoutDistinction: !prev.esupEsoutDistinction }))}
                                    disabled={!editMode}
                                />
                            </Grid>
                        </Grid>
                        <Grid size={6}>
                            <SimpleTextArea
                                title={i18n.description}
                                value={localScenario.description}
                                onChange={value => setLocalScenario({ ...localScenario, description: value })}
                                disabled={!editMode}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            <Grid container spacing={1} >
                <Grid size={6}>
                    {overviewPanel}
                </Grid>
                <Grid size={6}>
                    <Card round>
                        <EChart
                            id='waterfall-scenario-par'
                            options={optionsChart}
                        />
                    </Card>
                </Grid>
            </Grid>
            <Grid size={12}>
                <CardTable
                    title={i18n.rulesOfInternalRegulation}
                    rows={rulesSkimming}
                    headers={[
                        { key: 'nullValue', value: '' },
                        'ruleName',
                        { key: 'impactedLowWaterVolume', value: <span style={{ whiteSpace: 'pre-wrap' }}>{i18n.impactedLowWaterVolumeWrap}</span> },
                        { key: 'impactedNotLowWaterVolume', value: <span style={{ whiteSpace: 'pre-wrap' }}>{i18n.impactedNotLowWaterVolumeWrap}</span> },
                        'totalVolume',
                        'percentOfAUP',
                        { key: 'nullValue2', value: '' },
                    ]}
                    rowsPerPageOptions={nbPerPageLabelShort}
                    color={darkBlue}
                />
            </Grid>
            <Grid size={12}>
                <CardTable
                    title={i18n.arbitration}
                    rows={rulesArbitration}
                    headers={[
                        { key: 'nullValue', value: '' },
                        'ruleName',
                        { key: 'impactedLowWaterVolume', value: <span style={{ whiteSpace: 'pre-wrap' }}>{i18n.impactedLowWaterVolumeWrap}</span> },
                        { key: 'impactedNotLowWaterVolume', value: <span style={{ whiteSpace: 'pre-wrap' }}>{i18n.impactedNotLowWaterVolumeWrap}</span> },
                        { key: 'volumeExemptFromRule', value: <span style={{ whiteSpace: 'pre-wrap' }}>{i18n.volumeExemptFromRuleWrap}</span> },
                        'totalVolume',
                        'percentOfAUP',
                        { key: 'nullValue2', value: '' },
                    ]}
                    rowsPerPageOptions={nbPerPageLabelShort}
                    actions={editMode ? [{
                        tooltip: i18n.add,
                        icon: 'note_add',
                        color: 'white',
                        onClick: () => {
                            setRule({ ruleType: 'arbitrage', parameters: { operator: 2, unit: 1, periode: PAR.periode } })
                            setOpenRule(true)
                        },
                    }] : []}
                    lineActions={[{
                        icon: 'edit',
                        onClick: s => {
                            setRule(s)
                            setOpenRule(true)
                        },
                        displayed: editMode,
                    }]}
                    color={darkBlue}
                />
            </Grid>
            {getDialogScenario()}
            {openRule && (
                <RuleDialog
                    rule={{ ...rule, parameters: { ...rule.parameters, exceptions: rule?.parameters?.exceptions || localScenario?.linkRules?.find((lR) => lR.idRule === rule.id)?.parameters?.exceptions } }}
                    onChange={setRule}
                    datasRules={localDatasRules}
                    datasDiffRules={localDatasDiffRules}
                    onClose={() => setOpenRule(false)}
                    onValidate={onSaveRule}
                />
            )}
        </Stack>
    )
}

ScenarioDashboardApp.propTypes = {
    surveysWithStats: PropTypes.arrayOf(PropTypes.instanceOf(DtoSurveyWithStats)),
    managementUnits: PropTypes.arrayOf(PropTypes.instanceOf(DtoManagementUnit)),
    rulesScenarios: PropTypes.arrayOf(PropTypes.instanceOf(DtoRuleScenarios)),
}

export default ScenarioDashboardApp